import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Copy, Link2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { LW_DS_CLASS, LwButton, LwConfirmDialog, LwDataPanel, LwModal } from '@/components/lw';
import {
  AppraisalWorkspaceError,
  type AddPaymentLinkInput,
  type PaymentLinkReminderInput
} from '@/services/appraisalWorkspaceService';
import type {
  AppraisalCurrentPaymentLink,
  AppraisalPaymentLinks,
  AppraisalPaymentReminderOutcome,
  AppraisalPaymentReminderPreview,
  AppraisalPaymentRequest,
  AppraisalPaymentSource,
  AppraisalUnresolvedPaymentLink
} from '../../../../../supabase/functions/_shared/appraisal-workspace-contract';
import {
  OTHER_AMC,
  PAYMENT_LINK_AMCS,
  PAYMENT_LINK_FIELDS,
  checkPaymentLinkDraft,
  copyPaymentLink,
  type PaymentLinkDraft,
  type PaymentLinkField
} from './paymentLinkAmcs';
import { reminderLine, type FormatDate } from './paymentReminderLine';
import './appraisal.css';

/** What the reminder dialog reads from a link: a loan's request or a Payment links report row. */
export type ReminderChoice = Pick<AppraisalPaymentRequest, 'id' | 'reminderOpen' | 'amcName' | 'host' | 'firstSeenAt'>;

const SOURCE_LABELS: Record<AppraisalPaymentSource, string> = {
  mercury: 'Mercury order message',
  email: 'AMC email',
  manual: 'Added by staff'
};

function money(amount: number | null): string | null {
  return amount === null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

/** Header menu. Each item opens its dialog after the menu has closed and returned focus to the trigger. */
export function AppraisalActionsMenu({
  disabled,
  canSend,
  onSend,
  onAdd
}: {
  disabled: boolean;
  canSend: boolean;
  onSend: (trigger: HTMLButtonElement | null) => void;
  onAdd: (trigger: HTMLButtonElement | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    // A non-modal menu does not own focus when the pointer opened it; Escape must still close it.
    const closeOnWindowEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.target !== event.currentTarget) {
        return;
      }
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('keydown', closeOnWindowEscape, true);
    return () => window.removeEventListener('keydown', closeOnWindowEscape, true);
  }, [open]);

  const run = (action: (trigger: HTMLButtonElement | null) => void) => () => {
    window.setTimeout(() => action(triggerRef.current), 0);
  };

  return (
    <DropdownMenuPrimitive.Root modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button ref={triggerRef} type="button" className="btn-sm appraisal-actions-trigger" disabled={disabled}>
          Actions
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <DropdownMenuPrimitive.Content
            className="menu appraisal-actions-menu"
            align="end"
            sideOffset={6}
            collisionPadding={8}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          >
            <DropdownMenuPrimitive.Item asChild disabled={!canSend} onSelect={run(onSend)}>
              <button className="menu-item" type="button">
                <Send size={16} aria-hidden="true" />
                Send payment link reminder
              </button>
            </DropdownMenuPrimitive.Item>
            <DropdownMenuPrimitive.Item asChild onSelect={run(onAdd)}>
              <button className="menu-item" type="button">
                <Link2 size={16} aria-hidden="true" />
                Add payment link
              </button>
            </DropdownMenuPrimitive.Item>
          </DropdownMenuPrimitive.Content>
        </div>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

/** The loan's captured payment link: what the borrower will receive, and the last reminder. */
export function PaymentLinkPanel({
  links,
  canResolve,
  disabled,
  formatDate,
  onResolve
}: {
  links: AppraisalPaymentLinks;
  canResolve: boolean;
  disabled: boolean;
  formatDate: FormatDate;
  onResolve: (attemptId: string) => void;
}) {
  const [current, ...older] = links.requests;
  return (
    <LwDataPanel title="Payment link" ariaLabel="Payment link" className="appraisal-payment">
      {current ? (
        <>
          <div className="appraisal-payment__head">
            <strong>{current.amcName || 'Appraisal company'}</strong>
            <span>{current.host}</span>
          </div>
          <dl className="appraisal-facts">
            <div>
              <dt>Received</dt>
              <dd>{formatDate(current.firstSeenAt, true)}</dd>
            </div>
            <div>
              <dt>From</dt>
              <dd>{current.sources.map((source) => SOURCE_LABELS[source]).join(', ') || 'Not recorded'}</dd>
            </div>
            {current.reference.fileId && (
              <div>
                <dt>File ID</dt>
                <dd>{current.reference.fileId}</dd>
              </div>
            )}
            {current.reference.orderReferenceId && (
              <div>
                <dt>Order Reference ID</dt>
                <dd>{current.reference.orderReferenceId}</dd>
              </div>
            )}
            {money(current.amount) && (
              <div>
                <dt>Fee</dt>
                <dd>{money(current.amount)}</dd>
              </div>
            )}
            <div>
              <dt>Last reminder</dt>
              <dd>{reminderLine(current, formatDate)}</dd>
            </div>
          </dl>
          <div className="appraisal-payment__actions">
            <LwButton variant="sm" onClick={() => void copyPaymentLink(current)}>
              <Copy size={14} aria-hidden="true" />
              Copy link
            </LwButton>
            {canResolve && current.lastReminder && ['unknown', 'sending'].includes(current.lastReminder.status) && (
              <LwButton variant="sm" disabled={disabled} onClick={() => onResolve(current.lastReminder!.id)}>
                Close unconfirmed reminder
              </LwButton>
            )}
          </div>
          {older.length > 0 && (
            <p className="appraisal-help">
              {older.length === 1 ? 'One earlier link is' : `${older.length} earlier links are`} also on file. Choose
              between them when sending.
            </p>
          )}
        </>
      ) : (
        <p className="appraisal-help">No payment link has been captured for this loan. Add one from Actions.</p>
      )}
      {!links.reminderEnabled && (
        <p className="appraisal-help">The payment link reminder email is turned off in Email Notifications.</p>
      )}
    </LwDataPanel>
  );
}

function linkLabel(request: Pick<AppraisalPaymentRequest, 'amcName' | 'host' | 'firstSeenAt'>, formatDate: FormatDate): string {
  return `${request.amcName || request.host} · ${formatDate(request.firstSeenAt)}`;
}

/** Confirms the exact envelope before sending. A changed recipient list is shown again before anything is sent. */
export function SendPaymentReminderDialog({
  requests,
  returnFocusTo,
  formatDate,
  onClose,
  onPreview,
  onSend
}: {
  requests: ReminderChoice[];
  returnFocusTo: HTMLElement | null;
  formatDate: FormatDate;
  onClose: () => void;
  onPreview: (requestId: string) => Promise<AppraisalPaymentReminderPreview>;
  onSend: (input: PaymentLinkReminderInput) => Promise<AppraisalPaymentReminderOutcome>;
}) {
  const selectId = useId();
  const sendable = requests.filter((request) => !request.reminderOpen);
  const [requestId, setRequestId] = useState(sendable[0]?.id || '');
  const [preview, setPreview] = useState<AppraisalPaymentReminderPreview | null>(null);
  const [loading, setLoading] = useState(Boolean(requestId));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(requestId ? null : 'A reminder for this link is sending or unconfirmed.');
  const sendingRef = useRef(false);
  // One confirmation per link while the dialog is open: a retry of an unconfirmed send reuses it.
  const attempts = useRef(new Map<string, string>());
  const attemptFor = (id: string) => {
    if (!attempts.current.has(id)) {
      attempts.current.set(id, crypto.randomUUID());
    }
    return attempts.current.get(id)!;
  };

  useEffect(() => {
    if (!requestId) {
      return;
    }
    let live = true;
    setLoading(true);
    setPreview(null);
    setError(null);
    onPreview(requestId)
      .then((result) => {
        if (live) {
          setPreview(result);
        }
      })
      .catch((cause: unknown) => {
        if (live) {
          setError(cause instanceof Error ? cause.message : 'The recipients could not be checked.');
        }
      })
      .finally(() => {
        if (live) {
          setLoading(false);
        }
      });
    return () => {
      live = false;
    };
  }, [requestId, onPreview]);

  const confirm = async () => {
    if (!preview || sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      const outcome = await onSend({ requestId, attemptId: attemptFor(requestId), envelopeKey: preview.envelopeKey });
      if (outcome.status === 'sent') {
        toast.success(outcome.message);
        onClose();
        return;
      }
      if (outcome.status === 'failed') {
        attempts.current.delete(requestId);
      }
      setError(outcome.message);
    } catch (cause) {
      const changed = cause instanceof AppraisalWorkspaceError && cause.status === 409 ? cause.details?.preview : null;
      if (changed) {
        setPreview(changed as AppraisalPaymentReminderPreview);
      }
      setError(cause instanceof Error ? cause.message : 'The reminder could not be sent.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const ccText = !preview
    ? ''
    : preview.loanOfficer === 'copied'
      ? preview.cc.join(', ')
      : preview.loanOfficer === 'sender'
        ? 'Not needed: you are the loan officer'
        : 'Loan officer not copied: no single active portal user matches the loan officer in Encompass';

  return (
    <LwConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next && !sendingRef.current) {
          onClose();
        }
      }}
      title="Send payment link reminder"
      description="Emails the borrowers the appraisal company’s payment link again."
      confirmLabel={sending ? 'Sending…' : error && preview ? 'Try again' : 'Send reminder'}
      disabled={!preview || loading || sending}
      onConfirm={() => void confirm()}
      returnFocusTo={returnFocusTo}
    >
      <div className="appraisal-reminder">
        {sendable.length > 1 && (
          <div className="appraisal-reminder__choice">
            <label htmlFor={selectId}>Payment link</label>
            <select
              id={selectId}
              value={requestId}
              disabled={sending}
              onChange={(event) => setRequestId(event.target.value)}
            >
              {sendable.map((request) => (
                <option key={request.id} value={request.id}>
                  {linkLabel(request, formatDate)}
                </option>
              ))}
            </select>
          </div>
        )}
        {loading && (
          <p role="status" className="appraisal-help">
            <Loader2 size={14} className="animate-spin" aria-hidden="true" /> Checking the recipients…
          </p>
        )}
        {preview && (
          <dl className="appraisal-facts appraisal-reminder__envelope">
            <div>
              <dt>To</dt>
              <dd>{preview.to.join(', ')}</dd>
            </div>
            <div>
              <dt>Cc</dt>
              <dd>{ccText}</dd>
            </div>
            <div>
              <dt>Copy and replies</dt>
              <dd>{preview.replyTo}</dd>
            </div>
            <div>
              <dt>Property</dt>
              <dd>{preview.property || 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Appraisal company</dt>
              <dd>{preview.amcName || 'Not named'}</dd>
            </div>
            <div>
              <dt>Payment page</dt>
              <dd>{preview.host}</dd>
            </div>
            {preview.reference.fileId && preview.reference.orderReferenceId && (
              <div>
                <dt>File ID · Order Reference ID</dt>
                <dd>
                  {preview.reference.fileId} · {preview.reference.orderReferenceId}
                </dd>
              </div>
            )}
          </dl>
        )}
        {error && (
          <p role="alert" className="appraisal-error">
            {error}
          </p>
        )}
      </div>
    </LwConfirmDialog>
  );
}

const EMPTY_DRAFT: PaymentLinkDraft = { amc: '', url: '', amcName: '', fileId: '', orderReferenceId: '', amount: '' };

type TextField = Exclude<PaymentLinkField, 'amc'>;

/**
 * Add payment link's fields, shared by the loan's Appraisal tab and the Payment links report: the AMC first, then
 * what that AMC's email shows. Checked on Save; the server checks again. Admins can also use a captured AMC email
 * that no loan was matched to.
 */
export function PaymentLinkForm({
  formId,
  current,
  isAdmin,
  formatDate,
  busy,
  error,
  autoFocus = false,
  onListUnresolved,
  onSubmit
}: {
  formId: string;
  /** The loan's newest current link: what Replace would supersede. */
  current: AppraisalCurrentPaymentLink | undefined;
  isAdmin: boolean;
  formatDate: FormatDate;
  busy: boolean;
  /** The server's answer to the last save. */
  error: string | null;
  /** Focus the first AMC: the form replaced another screen in an open dialog. */
  autoFocus?: boolean;
  onListUnresolved: () => Promise<AppraisalUnresolvedPaymentLink[]>;
  onSubmit: (input: AddPaymentLinkInput) => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<PaymentLinkDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<PaymentLinkField, string>>>({});
  const [replace, setReplace] = useState(Boolean(current && !current.reminderOpen));
  const [unresolved, setUnresolved] = useState<AppraisalUnresolvedPaymentLink[] | null>(null);
  const fields = useRef<Partial<Record<PaymentLinkField, HTMLInputElement | null>>>({});
  const shownCurrent = useRef(current?.id);
  const amc = PAYMENT_LINK_AMCS.find((choice) => choice.name === draft.amc);
  const other = draft.amc === OTHER_AMC;

  useEffect(() => {
    // A different current link (a reload after a failed save, or a new capture) is never replaced without asking again.
    if (current?.id !== shownCurrent.current) {
      shownCurrent.current = current?.id;
      setReplace(false);
    }
  }, [current?.id]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }
    // After the dialog has placed its own focus.
    const frame = window.requestAnimationFrame(() => fields.current.amc?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    let live = true;
    onListUnresolved()
      .then((rows) => live && setUnresolved(rows))
      .catch(() => live && setUnresolved([]));
    return () => {
      live = false;
    };
  }, [isAdmin, onListUnresolved]);

  // Only a current link whose reminder is not sending or unconfirmed can be replaced.
  const replaceRequestId = replace && current && !current.reminderOpen ? current.id : undefined;
  const edit = (field: TextField | 'amount', value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }));
    if (field !== 'amount') {
      setErrors((previous) => ({ ...previous, [field]: undefined }));
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    const check = checkPaymentLinkDraft(draft);
    if (!check.ok) {
      setErrors(check.errors);
      const first = PAYMENT_LINK_FIELDS.find((field) => check.errors[field]);
      if (first) {
        fields.current[first]?.focus();
      }
      return;
    }
    setErrors({});
    onSubmit({ ...check.input, replaceRequestId });
  };
  const errorId = (field: PaymentLinkField) => `${id}-${field}-error`;
  const fieldError = (field: PaymentLinkField) =>
    errors[field] ? (
      <p id={errorId(field)} className="appraisal-field-error">
        {errors[field]}
      </p>
    ) : null;
  const textField = (field: TextField, label: string, options: { maxLength: number; url?: boolean; hint?: string }) => {
    const inputId = `${id}-${field}`;
    const hintId = options.hint ? `${inputId}-hint` : null;
    return (
      <div className="appraisal-payment-field">
        <label htmlFor={inputId}>{label}</label>
        <input
          id={inputId}
          ref={(node) => {
            fields.current[field] = node;
          }}
          type={options.url ? 'url' : 'text'}
          inputMode={options.url ? 'url' : undefined}
          placeholder={options.url ? 'https://' : undefined}
          autoComplete="off"
          spellCheck={false}
          maxLength={options.maxLength}
          value={draft[field]}
          disabled={busy}
          aria-invalid={errors[field] ? true : undefined}
          aria-describedby={[hintId, errors[field] ? errorId(field) : null].filter(Boolean).join(' ') || undefined}
          onChange={(event) => edit(field, event.target.value)}
        />
        {options.hint && (
          <p id={hintId!} className="appraisal-field-hint">
            {options.hint}
          </p>
        )}
        {fieldError(field)}
      </div>
    );
  };

  return (
    <>
      {isAdmin && unresolved && unresolved.length > 0 && (
        <section className="appraisal-unmatched" aria-labelledby={`${id}-unmatched`}>
          <h4 id={`${id}-unmatched`}>AMC emails not matched to a loan</h4>
          <ul>
            {unresolved.map((row) => (
              <li key={row.id}>
                <span>
                  <strong>{row.amcName || row.host}</strong>
                  <small>
                    {formatDate(row.receivedAt, true)}
                    {row.borrowerText ? ` · ${row.borrowerText}` : ''}
                    {row.propertyText ? ` · ${row.propertyText}` : ''}
                  </small>
                </span>
                <LwButton variant="sm" disabled={busy} onClick={() => onSubmit({ observationId: row.id, replaceRequestId })}>
                  Use for this loan
                </LwButton>
              </li>
            ))}
          </ul>
        </section>
      )}
      <form id={formId} noValidate onSubmit={submit} className="appraisal-upload-form appraisal-payment-form">
        <fieldset className="appraisal-amc">
          <legend>AMC</legend>
          <div className="appraisal-amc__tiles">
            {[...PAYMENT_LINK_AMCS.map((choice) => choice.name), OTHER_AMC].map((name, index) => (
              <label
                key={name}
                className="appraisal-amc__tile"
                data-checked={draft.amc === name || undefined}
                data-invalid={errors.amc ? true : undefined}
                data-disabled={busy || undefined}
              >
                <input
                  ref={
                    index === 0
                      ? (node) => {
                          fields.current.amc = node;
                        }
                      : undefined
                  }
                  type="radio"
                  name={`${id}-amc`}
                  value={name}
                  checked={draft.amc === name}
                  disabled={busy}
                  aria-invalid={errors.amc ? true : undefined}
                  aria-describedby={errors.amc ? errorId('amc') : undefined}
                  onChange={() => {
                    setDraft((previous) => ({ ...previous, amc: name }));
                    setErrors({});
                  }}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
          {fieldError('amc')}
        </fieldset>
        {amc?.genericPage && (
          <fieldset className="appraisal-ids">
            <legend>From {amc.name}’s email</legend>
            <p className="appraisal-field-hint">
              {/* Lets a narrow screen break the page between its host and path. */}
              Payment page: {new URL(amc.genericPage).host}
              <wbr />
              {new URL(amc.genericPage).pathname}, the same for every {amc.name} order.
            </p>
            <div className="appraisal-ids__pair">
              {textField('fileId', 'File ID', { maxLength: 40 })}
              {textField('orderReferenceId', 'Order Reference ID', { maxLength: 40 })}
            </div>
          </fieldset>
        )}
        {other && textField('amcName', 'AMC name', { maxLength: 120 })}
        {(other || (amc && !amc.genericPage)) &&
          textField('url', 'Payment link', {
            maxLength: 2048,
            url: true,
            hint: amc ? `The link is on ${amc.hosts.join(' or ')}.` : undefined
          })}
        {other && (
          <>
            {textField('fileId', 'File ID (if the email lists one)', { maxLength: 40 })}
            {textField('orderReferenceId', 'Order Reference ID (if the email lists one)', { maxLength: 40 })}
          </>
        )}
        {draft.amc && (
          <div className="appraisal-payment-field">
            <label htmlFor={`${id}-amount`}>Fee (optional)</label>
            <input
              id={`${id}-amount`}
              inputMode="decimal"
              autoComplete="off"
              maxLength={20}
              value={draft.amount}
              disabled={busy}
              onChange={(event) => edit('amount', event.target.value)}
            />
          </div>
        )}
        {/* Shown before an AMC is chosen too: an admin's captured email saves without one. */}
        {current && (
          <div className="appraisal-payment-replace">
            <label htmlFor={`${id}-replace`} className="appraisal-checkbox">
              <input
                id={`${id}-replace`}
                type="checkbox"
                checked={replaceRequestId !== undefined}
                disabled={busy || current.reminderOpen}
                aria-describedby={current.reminderOpen ? `${id}-replace-note` : undefined}
                onChange={(event) => setReplace(event.target.checked)}
              />
              Replace the current link from {current.amcName || current.host}
            </label>
            {current.reminderOpen && (
              <p id={`${id}-replace-note`} className="appraisal-field-hint">
                A reminder for the current link is sending or unconfirmed, so that link stays. This one is added next to it.
              </p>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="appraisal-error">
            {error}
          </p>
        )}
      </form>
    </>
  );
}

/** The loan's own Add payment link, from the Appraisal tab's Actions. */
export function AddPaymentLinkModal({
  current,
  isAdmin,
  formatDate,
  returnFocusTo,
  onClose,
  onAdd,
  onListUnresolved
}: {
  current: AppraisalCurrentPaymentLink | undefined;
  isAdmin: boolean;
  formatDate: FormatDate;
  /** The Actions trigger: the menu has already given focus up when this opens. */
  returnFocusTo?: HTMLElement | null;
  onClose: () => void;
  onAdd: (input: AddPaymentLinkInput) => Promise<void>;
  onListUnresolved: () => Promise<AppraisalUnresolvedPaymentLink[]>;
}) {
  const formId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saving = useRef(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const save = async (input: AddPaymentLinkInput) => {
    if (saving.current) {
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await onAdd(input);
      // A save that finishes after this dialog closed must not close one opened since.
      if (live.current) {
        onClose();
      }
    } catch (cause) {
      if (live.current) {
        setError(cause instanceof Error ? cause.message : 'The payment link could not be saved.');
      }
    } finally {
      saving.current = false;
      if (live.current) {
        setBusy(false);
      }
    }
  };

  return (
    <LwModal
      className="appraisal-modal"
      eyebrow="APPRAISAL PAYMENT"
      title="Add payment link"
      sub="The borrower gets the link only when you send a reminder."
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      footer={<PaymentLinkFooter formId={formId} busy={busy} onCancel={onClose} />}
    >
      <PaymentLinkForm
        formId={formId}
        current={current}
        isAdmin={isAdmin}
        formatDate={formatDate}
        busy={busy}
        error={error}
        onListUnresolved={onListUnresolved}
        onSubmit={(input) => void save(input)}
      />
    </LwModal>
  );
}

/** Cancel and Save link, for a dialog showing PaymentLinkForm. */
export function PaymentLinkFooter({ formId, busy, onCancel }: { formId: string; busy: boolean; onCancel: () => void }) {
  return (
    <>
      <LwButton variant="sm" onClick={onCancel}>
        Cancel
      </LwButton>
      <LwButton variant="sm-solid" type="submit" form={formId} disabled={busy}>
        {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
        {busy ? 'Saving…' : 'Save link'}
      </LwButton>
    </>
  );
}

/** Admin: close a reminder whose delivery could not be confirmed, after checking Resend. */
export function ResolveReminderModal({
  attemptId,
  onClose,
  onResolve
}: {
  attemptId: string;
  onClose: () => void;
  onResolve: (input: { attemptId: string; delivered: boolean; note: string }) => Promise<void>;
}) {
  const noteId = useId();
  const groupId = useId();
  const [delivered, setDelivered] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <LwModal
      className="appraisal-modal"
      eyebrow="PAYMENT LINK REMINDER"
      title="Close the unconfirmed reminder"
      sub="Check Email Notifications or Resend first. Closing it lets staff send another reminder."
      onClose={onClose}
      footer={
        <>
          <LwButton variant="sm" onClick={onClose}>
            Cancel
          </LwButton>
          <LwButton
            variant="sm-solid"
            disabled={busy || delivered === null || note.trim().length < 10}
            onClick={() => {
              setBusy(true);
              setError(null);
              void onResolve({ attemptId, delivered: delivered === true, note: note.trim() })
                .then(onClose)
                .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'The reminder could not be closed.'))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Saving…' : 'Close reminder'}
          </LwButton>
        </>
      }
    >
      <div className="appraisal-upload-form">
        <fieldset className="appraisal-choice" aria-describedby={groupId}>
          <legend>What happened?</legend>
          <label>
            <input type="radio" name={groupId} checked={delivered === true} onChange={() => setDelivered(true)} /> It was
            delivered
          </label>
          <label>
            <input type="radio" name={groupId} checked={delivered === false} onChange={() => setDelivered(false)} /> It was
            not delivered
          </label>
        </fieldset>
        <label htmlFor={noteId}>Note</label>
        <textarea id={noteId} value={note} rows={3} maxLength={2000} onChange={(event) => setNote(event.target.value)} />
        <p id={groupId} className="appraisal-help">
          Your name and the date are recorded with the note.
        </p>
        {error && (
          <p role="alert" className="appraisal-error">
            {error}
          </p>
        )}
      </div>
    </LwModal>
  );
}
